import React from "react";
import {
  Copy, ShieldCheck, AlertTriangle, ShieldX, HelpCircle, Info,
  Flame, History,
} from "lucide-react";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";

/**
 * CmssCard — Copycat Mint Similarity Score (F3).
 *
 * Surfaces the CMSS modifier from /api/scan:
 *   report.risk_modifiers.cmss = {
 *     enabled, ok, band: "PASS"|"WARN"|"FAIL"|null,
 *     penalty, skipped_reason,
 *     details: {
 *       candidate: {name, symbol},
 *       matched: {name, symbol, source} | null,
 *       method: "levenshtein"|"jaro_winkler"|null,
 *       score: number|null,
 *       trending_size, history_size,
 *     }
 *   }
 *
 * FAIL = mimics a DexScreener trending/boosted token.
 * WARN = mimics a recent solscanner alert (digest DB).
 * PASS = no close match.
 */

function bandMeta(band) {
  switch (band) {
    case "PASS":
      return { label: "Original name", variant: "success", icon: ShieldCheck };
    case "WARN":
      return { label: "Echoes recent alert", variant: "warning", icon: AlertTriangle };
    case "FAIL":
      return { label: "Mimics trending token", variant: "danger", icon: ShieldX };
    default:
      return { label: "Not scored", variant: "muted", icon: HelpCircle };
  }
}

function humanizeSkippedReason(reason) {
  if (!reason) return "";
  if (reason === "disabled") return "CMSS is disabled on the backend.";
  if (reason === "no_name_symbol") return "Token has no name or symbol to compare.";
  return reason;
}

function sourceLabel(source) {
  if (source === "dexscreener_boosts") return "DexScreener boosted/trending list";
  if (source === "digest_recent_alerts") return "Recent solscanner alerts (digest DB)";
  return source || "—";
}

function methodLabel(method) {
  if (method === "levenshtein") return "Levenshtein";
  if (method === "jaro_winkler") return "Jaro–Winkler";
  return null;
}

export function CmssCard({ cmss }) {
  if (!cmss) return null;
  const meta = bandMeta(cmss.band);
  const Icon = meta.icon;
  const d = cmss.details || {};
  const isScored = cmss.band === "PASS" || cmss.band === "WARN" || cmss.band === "FAIL";
  const reasonText = humanizeSkippedReason(cmss.skipped_reason);

  const cand = d.candidate || {};
  const matched = d.matched || null;
  const method = methodLabel(d.method);

  return (
    <Card className="p-0" data-testid="cmss-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Copy size={16} className="mt-0.5 text-[hsl(var(--muted))]" />
            <div>
              <CardTitle>Copycat mint similarity</CardTitle>
              <CardDescription>
                Checks the token name & symbol against trending Solana tokens
                and recent solscanner alerts.
              </CardDescription>
            </div>
          </div>
          <Badge variant={meta.variant} className="gap-1">
            <Icon size={12} /> {meta.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isScored ? (
          <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-3">
            <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted))]">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>{reasonText || "CMSS could not score this token."}</span>
            </div>
          </div>
        ) : (
          <>
            {/* Candidate vs matched (when a hit) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div
                className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
                data-testid="cmss-candidate"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
                  Candidate
                </div>
                <div className="mt-1 font-mono text-sm break-all">
                  {cand.name || "—"}
                  {cand.symbol ? (
                    <span className="ml-2 text-[hsl(var(--muted))]">
                      · {cand.symbol}
                    </span>
                  ) : null}
                </div>
              </div>

              {matched ? (
                <div
                  className="px-3 py-2 rounded-md border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.06)]"
                  data-testid="cmss-matched"
                >
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--danger))]">
                    {cmss.band === "FAIL" ? (
                      <>
                        <Flame size={11} /> Matches trending
                      </>
                    ) : (
                      <>
                        <History size={11} /> Matches recent alert
                      </>
                    )}
                  </div>
                  <div className="mt-1 font-mono text-sm break-all">
                    {matched.name || "—"}
                    {matched.symbol ? (
                      <span className="ml-2 text-[hsl(var(--muted))]">
                        · {matched.symbol}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div
                  className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
                  data-testid="cmss-no-match"
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
                    Closest match
                  </div>
                  <div className="mt-1 text-sm text-[hsl(var(--muted))]">
                    None within thresholds
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px] text-[hsl(var(--muted))]">
              <div>
                Trending sample:{" "}
                <span className="font-mono tabular-nums text-[hsl(var(--fg))]">
                  {d.trending_size ?? 0}
                </span>
              </div>
              <div>
                Recent alerts:{" "}
                <span className="font-mono tabular-nums text-[hsl(var(--fg))]">
                  {d.history_size ?? 0}
                </span>
              </div>
            </div>

            {matched ? (
              <>
                <Separator />
                <div className="text-[11px] text-[hsl(var(--muted))] space-y-1">
                  <div>
                    Source:{" "}
                    <span className="text-[hsl(var(--fg))]">
                      {sourceLabel(matched.source)}
                    </span>
                  </div>
                  {method ? (
                    <div>
                      Method:{" "}
                      <span className="font-mono text-[hsl(var(--fg))]">
                        {method}
                      </span>
                      {d.score != null ? (
                        <>
                          {" "}· score{" "}
                          <span className="font-mono text-[hsl(var(--fg))] tabular-nums">
                            {Number(d.score).toFixed(d.method === "levenshtein" ? 0 : 3)}
                          </span>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            {cmss.penalty ? (
              <>
                <Separator />
                <div className="text-[11px] text-[hsl(var(--muted))]">
                  Score penalty applied:{" "}
                  <span className="font-mono text-[hsl(var(--warning))]">
                    −{cmss.penalty}
                  </span>
                </div>
              </>
            ) : null}
          </>
        )}

        <p className="text-[10px] text-[hsl(var(--muted))]">
          Matching uses Levenshtein edit distance and Jaro–Winkler similarity on
          normalised (lowercased, alphanumeric-only) names and symbols. Exact
          re-listings are never flagged.
        </p>
      </CardContent>
    </Card>
  );
}

export default CmssCard;
